import React, { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { identifyUser, resetUser, trackEvent } from "./analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authClient = axios.create({ baseURL: API, withCredentials: true, timeout: 30000 });

const AuthCtx = createContext({});

function adminIdentifyProps(user) {
  if (!user) return {};
  return {
    email:    user.email,
    role:     user.role,
    org_id:   user.org_id,
    is_admin: true,
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined);    // undefined = checking
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      // Iter276.5 — Treat the anon-visitor 401 as a normal "no session"
      // response so it doesn't surface in DevTools as a red XHR error.
      // This call is HOW we detect "logged in?" — there's nothing
      // exceptional about a 401 here. Mirrors the pattern in
      // userAuth.js refresh().
      const { data, status } = await authClient.get("/admin/auth/me", {
        validateStatus: (s) => s === 200 || s === 401,
      });
      if (status === 401) {
        setUser(null);
      } else {
        setUser(data.user);
        if (data.user) identifyUser(data.user.id, adminIdentifyProps(data.user));
      }
    } catch (_) {
      // Network or 5xx — keep silent for UX.
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const login = async (email, password, totp_code, turnstile_token) => {
    const { data } = await authClient.post(
      "/admin/auth/login",
      { email, password, totp_code, turnstile_token },
    );
    if (data.requires_totp) return { requires_totp: true };
    setUser(data.user);
    if (data.user) {
      identifyUser(data.user.id, adminIdentifyProps(data.user));
      trackEvent("admin_login", { role: data.user.role });
    }
    return { user: data.user };
  };

  const logout = async () => {
    try { await authClient.post("/admin/auth/logout"); } catch (_) {}
    setUser(null);
    resetUser();
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, logout, refresh, authClient }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
